/**
 * Horizon overrides for Shopify.actions:
 * - updateCart: emit events from the cart drawer scope.
 * - openCart: open the cart drawer (fall back to /cart when absent).
 */

import { StandardEvents } from '@shopify/events';

const cartDrawerBridgedEvent = Symbol.for('tfp.cart-drawer-bridged-event');
let cartDrawerRuntime;

const loadCartDrawerRuntime = () => {
  cartDrawerRuntime ||= import('@theme/cart-drawer').catch((error) => {
    cartDrawerRuntime = undefined;
    throw error;
  });
  return cartDrawerRuntime;
};

const getCartFallbackUrl = () => window.Theme?.routes?.cart_url || '/cart';

async function getCartDrawer() {
  const drawer = document.querySelector('tfp-cart-drawer-component#cart-drawer');
  if (!(drawer instanceof HTMLElement)) return null;

  if (!customElements.get('tfp-cart-drawer-component')) await loadCartDrawerRuntime();
  customElements.upgrade(drawer);

  return typeof drawer.open === 'function' ? drawer : null;
}

function bridgeCartLinesUpdate(event) {
  if (event.action !== 'add' || customElements.get('tfp-cart-drawer-component')) return;

  const drawer = document.querySelector('tfp-cart-drawer-component#cart-drawer');
  if (!(drawer instanceof HTMLElement)) return;

  event[cartDrawerBridgedEvent] = true;
  void loadCartDrawerRuntime()
    .then(() => {
      const controller = document.querySelector('cart-drawer-component[data-lazy-section-controller]');
      if (controller && typeof controller.handleCartLinesUpdate === 'function') {
        controller.handleCartLinesUpdate(event);
        return;
      }

      throw new Error('Cart drawer controller unavailable after runtime load.');
    })
    .catch(() => {
      event.promise
        ?.then(({ detail }) => {
          if (!detail?.didError) window.location.assign(getCartFallbackUrl());
        })
        .catch(() => undefined);
    });
}

function init() {
  const actions = window.Shopify?.actions;
  if (!actions) return;

  actions.updateCart.configure({
    eventTarget: () => document.querySelector('tfp-cart-drawer-component#cart-drawer') ?? document,
  });

  actions.openCart.configure({
    async handler() {
      try {
        const drawer = await getCartDrawer();
        if (drawer) {
          await drawer.open({ fallbackOnError: true });
          return;
        }
      } catch (error) {
        console.warn('[cart-drawer] Runtime unavailable:', error);
      }

      window.location.assign(getCartFallbackUrl());
    },
  });
}

// Run immediately if the standard-actions bundle has already attached
// `Shopify.actions`; otherwise wait for DOMContentLoaded, which fires after
// all module scripts have executed regardless of document order.
if (window.Shopify?.actions) {
  init();
} else {
  document.addEventListener('DOMContentLoaded', init, { once: true });
}

document.addEventListener(StandardEvents.cartLinesUpdate, bridgeCartLinesUpdate, true);
